import React,{createContext,useCallback,useContext,useEffect,useState} from 'react';
import {supabase} from '../supabase/supabase';
import {useSupabaseAuth} from './SupabaseAuthContext';
import {
  fetchPickupNotifications,
  markPickupNotificationRead
} from '../services/pickupService';
import {
  updateStripeOrderStatus,
  verifySellerPlatformCheckoutEligibility
} from '../services/stripePaymentService';

const SupabaseDataContext=createContext();

export const useSupabaseData=()=>{
  const context=useContext(SupabaseDataContext);

  if (!context) {
    throw new Error(
      'useSupabaseData must be used within a SupabaseDataProvider'
    );
  }

  return context;
};

const callAdminRpc=async(functionName,params)=>{
  const {data,error}=await supabase.rpc(functionName,params);

  if (error) throw error;

  return data;
};

const getResultData=(result,fallback=[])=>{
  if (result.status==='fulfilled' && !result.value.error) {
    return result.value.data || fallback;
  }

  if (result.status==='rejected') {
    console.error('Supabase request failed:',result.reason);
  } else if (result.value.error) {
    console.error('Supabase request failed:',result.value.error);
  }

  return fallback;
};

const normalizeSeller=(seller={})=>({
  ...seller,
  profile_images:seller.profile_images
    || (seller.profile_image ? [seller.profile_image] : []),
  is_active:seller.is_active!==false
});

const normalizeOrder=(order={},sellerRows=[],currentUser=null,currentProfile=null)=>{
  const seller=order.seller
    || sellerRows.find((item)=>item.id===order.seller_id);

  const buyer=order.buyer
    || (
      currentUser?.id===order.buyer_id
        ? currentProfile
        : null
    );

  return {
    ...order,
    seller:seller ? normalizeSeller(seller) : order.seller,
    buyer:buyer || order.buyer
  };
};

const ORDER_SELECT=[
  '*',
  'seller:sellers!orders_seller_id_fkey(*)',
  'buyer:users!orders_buyer_id_fkey(*)'
].join(',');

const ORDER_BASE_SELECT=ORDER_SELECT.split(',')[0];

const getMarketplaceSellers=async()=>{
  const publicSellerResult=await supabase.rpc(
    'get_public_seller_shop_profiles_1793051000000'
  );

  if (!publicSellerResult.error) {
    return (publicSellerResult.data || []).map(normalizeSeller);
  }

  console.error(
    'Public seller shop lookup failed:',
    publicSellerResult.error
  );

  const sellerSelect=[
    'id',
    'user_id',
    'business_name',
    'description',
    'location',
    'service_area',
    'availability',
    'category',
    'average_price',
    'profile_image',
    'profile_images',
    'rating',
    'review_count',
    'is_active',
    'created_at',
    'updated_at',
    'completed_orders_count',
    'policy_settings',
    'shop_username',
    'min_order_window',
    'is_delivery_enabled',
    'delivery_charge',
    'stripe_account_id',
    'stripe_onboarding_complete',
    'platform_activation_paid_at',
    'platform_payments_enabled',
    'weekly_availability',
    'blackout_dates',
    'pickup_weekly_availability',
    'pickup_blackout_dates'
  ];

  const sellerDataSelect=sellerSelect
    .filter((column)=>![
      'stripe_account_id',
      'stripe_onboarding_complete',
      'platform_activation_paid_at',
      'platform_payments_enabled'
    ].includes(column))
    .join(',');

  const directResult=await supabase
    .from('sellers')
    .select(sellerDataSelect)
    .eq('is_active',true)
    .limit(100);

  if (!directResult.error) {
    return (directResult.data || []).map(normalizeSeller);
  }

  console.error(
    'Direct seller request failed:',
    directResult.error
  );

  const viewResult=await supabase
    .from('marketplace_listings')
    .select('*')
    .limit(100);

  if (viewResult.error) {
    console.error(
      'Marketplace seller view failed:',
      viewResult.error
    );
    return [];
  }

  return (viewResult.data || []).map(normalizeSeller);
};

const getOwnedSellerRows=async(userId)=>{
  const ownedSellerResult=await supabase.rpc(
    'seller_get_own_profile_1793041000000'
  );

  if (!ownedSellerResult.error) {
    const data=ownedSellerResult.data;

    return Array.isArray(data)
      ? data
      : data
        ? [data]
        : [];
  }

  console.error(
    'Owned seller request failed:',
    ownedSellerResult.error
  );

  const sellerSelect=[
    'id',
    'user_id',
    'business_name',
    'description',
    'location',
    'service_area',
    'whatsapp_number',
    'availability',
    'category',
    'average_price',
    'profile_image',
    'profile_images',
    'rating',
    'review_count',
    'is_active',
    'created_at',
    'updated_at',
    'completed_orders_count',
    'policy_settings',
    'shop_username',
    'min_order_window',
    'is_delivery_enabled',
    'delivery_charge',
    'weekly_availability',
    'blackout_dates',
    'pickup_weekly_availability',
    'pickup_blackout_dates'
  ].join(',');

  const fallbackResult=await supabase
    .from('sellers')
    .select(sellerSelect)
    .eq('user_id',userId);

  if (fallbackResult.error) {
    console.error(
      'Owned seller fallback request failed:',
      fallbackResult.error
    );
    return [];
  }

  return fallbackResult.data || [];
};

const getOrderChatProfiles=async(userIds)=>{
  const profileIds=[...new Set(userIds.filter(Boolean))];

  if (!profileIds.length) return {};

  const {data,error}=await supabase.rpc(
    'get_order_chat_profiles_1793041000000',
    {p_user_ids:profileIds}
  );

  if (error) {
    console.error('Order chat profile request failed:',error);
    return {};
  }

  return (data || []).reduce((profiles,userProfile)=>{
    profiles[userProfile.id]=userProfile;
    return profiles;
  },{});
};

const getCategories=async(sellerRows)=>{
  const categoryResult=await supabase
    .from('categories')
    .select('*')
    .order('name');

  if (!categoryResult.error && categoryResult.data?.length) {
    return categoryResult.data;
  }

  if (categoryResult.error) {
    console.error('Category request failed:',categoryResult.error);
  }

  const sellerCategories=[
    ...new Set(
      sellerRows
        .map((seller)=>seller.category?.trim())
        .filter(Boolean)
    )
  ];

  return sellerCategories.map((name)=>({
    id:`seller-category-${name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`,
    name,
    slug:name.toLowerCase().replace(/[^a-z0-9]+/g,'-'),
    icon_name:'Box'
  }));
};

export const SupabaseDataProvider=({children})=>{
  const {user,profile}=useSupabaseAuth();
  const [sellers,setSellers]=useState([]);
  const [offerings,setOfferings]=useState([]);
  const [categories,setCategories]=useState([]);
  const [reviews,setReviews]=useState([]);
  const [orders,setOrders]=useState([]);
  const [messages,setMessages]=useState([]);
  const [pickupNotifications,setPickupNotifications]=useState([]);
  const [isDataReady,setIsDataReady]=useState(false);

  const refreshPickupNotifications=useCallback(async()=>{
    if (!user) {
      setPickupNotifications([]);
      return;
    }

    try {
      const notifications=await fetchPickupNotifications();
      setPickupNotifications(notifications);
    } catch (error) {
      console.error(
        'Pickup notifications refresh failed:',
        error
      );
    }
  },[user]);

  const refreshData=useCallback(async()=>{
    setIsDataReady(false);

    try {
      const sellerRows=await getMarketplaceSellers();

      const results=await Promise.allSettled([
        supabase
          .from('offerings')
          .select('*')
          .eq('is_active',true)
          .limit(200),
        getCategories(sellerRows),
        supabase
          .from('reviews')
          .select('*')
          .order('created_at',{ascending:false}),
        user
          ? supabase
            .from('orders')
            .select(ORDER_BASE_SELECT)
            .eq('buyer_id',user.id)
            .order('created_at',{ascending:false})
          : Promise.resolve({data:[],error:null}),
        user
          ? supabase
            .from('messages')
            .select('*')
            .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
            .order('created_at')
          : Promise.resolve({data:[],error:null})
      ]);

      const offeringRows=getResultData(results[0]);
      const categoryRows=results[1].status==='fulfilled'
        ? results[1].value
        : [];
      const reviewRows=getResultData(results[2]);
      const buyerOrders=getResultData(results[3]);
      const messageRows=getResultData(results[4]);

      let allSellers=sellerRows;
      let sellerOrders=[];

      if (user) {
        const ownedSellerResult=await getOwnedSellerRows(user.id);
        const sellerMap=new Map(
          allSellers.map((seller)=>[seller.id,seller])
        );

        (ownedSellerResult || []).forEach((seller)=>{
          sellerMap.set(seller.id,{
            ...sellerMap.get(seller.id),
            ...normalizeSeller(seller)
          });
        });

        allSellers=Array.from(sellerMap.values());

        const ownedSellerIds=(ownedSellerResult || [])
          .map((seller)=>seller.id)
          .filter(Boolean);

        if (ownedSellerIds.length>0) {
          const sellerOrdersResult=await supabase
            .from('orders')
            .select(ORDER_BASE_SELECT)
            .in('seller_id',ownedSellerIds)
            .order('created_at',{ascending:false});

          if (sellerOrdersResult.error) {
            console.error(
              'Seller orders request failed:',
              sellerOrdersResult.error
            );
          } else {
            sellerOrders=sellerOrdersResult.data || [];
          }
        }
      }

      const rawOrders=Array.from(
        new Map(
          [...buyerOrders,...sellerOrders]
            .filter((order)=>order?.id)
            .map((order)=>[
              order.id,
              order
            ])
        ).values()
      );

      const knownSellerIds=new Set(
        allSellers.map((seller)=>seller.id)
      );
      const missingSellerIds=[
        ...new Set(
          rawOrders
            .map((order)=>order.seller_id)
            .filter((sellerId)=>sellerId && !knownSellerIds.has(sellerId))
        )
      ];

      if (missingSellerIds.length>0) {
        const sellerSelect=[
          'id',
          'user_id',
          'business_name',
          'description',
          'location',
          'service_area',
          'whatsapp_number',
          'availability',
          'category',
          'average_price',
          'profile_image',
          'profile_images',
          'rating',
          'review_count',
          'is_active',
          'created_at',
          'updated_at',
          'completed_orders_count',
          'policy_settings',
          'shop_username',
          'min_order_window',
          'is_delivery_enabled',
          'delivery_charge'
        ].join(',');

        const missingSellerResult=await supabase
          .from('sellers')
          .select(sellerSelect)
          .in('id',missingSellerIds);

        if (missingSellerResult.error) {
          console.error(
            'Order seller details request failed:',
            missingSellerResult.error
          );
        } else {
          allSellers=[
            ...allSellers,
            ...(missingSellerResult.data || []).map(normalizeSeller)
          ];
        }
      }

      const sellerUserIds=allSellers
        .filter((seller)=>rawOrders.some(
          (order)=>order.seller_id===seller.id
        ))
        .map((seller)=>seller.user_id);

      const participantIds=[
        ...rawOrders.map((order)=>order.buyer_id),
        ...sellerUserIds,
        ...messageRows.flatMap((message)=>[
          message.sender_id,
          message.receiver_id
        ])
      ];

      const participantProfiles=user
        ? await getOrderChatProfiles(participantIds)
        : {};

      if (user?.id && profile) {
        participantProfiles[user.id]={
          id:user.id,
          ...profile
        };
      }

      const uniqueOrders=rawOrders.map((order)=>{
        const normalizedOrder=normalizeOrder(
          order,
          allSellers,
          user,
          profile
        );
        const seller=normalizedOrder.seller;
        const buyerProfile=participantProfiles[order.buyer_id];

        return {
          ...normalizedOrder,
          buyer:normalizedOrder.buyer || buyerProfile || null,
          seller:seller
            ? {
              ...seller,
              owner_profile:participantProfiles[seller.user_id] || null
            }
            : seller
        };
      });

      const enrichedMessages=messageRows.map((message)=>({
        ...message,
        sender_profile:participantProfiles[message.sender_id] || null,
        receiver_profile:participantProfiles[message.receiver_id] || null
      }));

      setSellers(allSellers);
      setOfferings(offeringRows);
      setCategories(categoryRows);
      setReviews(reviewRows);
      setOrders(uniqueOrders);
      setMessages(enrichedMessages);
      await refreshPickupNotifications();
    } catch (error) {
      console.error(
        'Marketplace data refresh failed:',
        error
      );
      setSellers([]);
      setOfferings([]);
      setCategories([]);
      setReviews([]);
      setOrders([]);
      setMessages([]);
    } finally {
      setIsDataReady(true);
    }
  },[user,profile,refreshPickupNotifications]);

  useEffect(()=>{
    refreshData();
  },[refreshData]);

  useEffect(()=>{
    if (!user) return undefined;

    const channel=supabase
      .channel(`order-status-updates-${user.id}`)
      .on(
        'postgres_changes',
        {
          event:'UPDATE',
          schema:'public',
          table:'orders'
        },
        ()=>{
          refreshData();
        }
      )
      .subscribe();

    return ()=>{
      supabase.removeChannel(channel);
    };
  },[user,refreshData]);

  useEffect(()=>{
    const handleOrdersChanged=()=>{
      refreshData();
    };

    window.addEventListener(
      'sohlocal:orders-changed',
      handleOrdersChanged
    );

    return ()=>{
      window.removeEventListener(
        'sohlocal:orders-changed',
        handleOrdersChanged
      );
    };
  },[refreshData]);

  useEffect(()=>{
    if (!user) return undefined;

    const interval=window.setInterval(()=>{
      refreshPickupNotifications();
    },15000);

    return ()=>window.clearInterval(interval);
  },[user,refreshPickupNotifications]);

  const updateSellerProfile=useCallback(async(sellerId,updates)=>{
    const {data,error}=await supabase
      .from('sellers')
      .update(updates)
      .eq('id',sellerId)
      .select()
      .single();

    if (error) throw error;

    setSellers((current)=>current.map((seller)=>(
      seller.id===sellerId
        ? {...seller,...data}
        : seller
    )));

    return data;
  },[]);

  const addOffering=useCallback(async(data)=>{
    const {data:offering,error}=await supabase
      .from('offerings')
      .insert(data)
      .select()
      .single();

    if (error) throw error;

    setOfferings((current)=>[...current,offering]);

    return offering;
  },[]);

  const updateOffering=useCallback(async(id,updates)=>{
    const {data,error}=await supabase
      .from('offerings')
      .update(updates)
      .eq('id',id)
      .select()
      .single();

    if (error) throw error;

    setOfferings((current)=>current.map((offering)=>(
      offering.id===id ? data : offering
    )));

    return data;
  },[]);

  const deleteOffering=useCallback(async(id)=>{
    const {error}=await supabase
      .from('offerings')
      .delete()
      .eq('id',id);

    if (error) throw error;

    setOfferings((current)=>current.filter(
      (offering)=>offering.id!==id
    ));
  },[]);

  const createOrder=useCallback(async(data)=>{
    const sellerId=data.seller_id || data.sellerId;

    if (!sellerId) {
      throw new Error(
        'A valid seller ID is required to create an order.'
      );
    }

    const orderPayload={
      ...data,
      buyer_id:data.buyer_id || user?.id,
      seller_id:sellerId
    };

    if (!orderPayload.buyer_id) {
      throw new Error(
        'A valid buyer ID is required to create an order.'
      );
    }

    if (orderPayload.payment_mode==='platform') {
      await verifySellerPlatformCheckoutEligibility(sellerId);
    }

    const {data:createdOrder,error}=await supabase
      .from('orders')
      .insert(orderPayload)
      .select()
      .single();

    if (error) throw error;

    let order=createdOrder;

    const enrichedResult=await supabase
      .from('orders')
      .select(ORDER_BASE_SELECT)
      .eq('id',createdOrder.id)
      .single();

    if (!enrichedResult.error && enrichedResult.data) {
      order=enrichedResult.data;
    } else if (enrichedResult.error) {
      console.error(
        'Order relationship refresh failed:',
        enrichedResult.error
      );
    }

    order=normalizeOrder(order,sellers,user,profile);

    setOrders((current)=>[
      order,
      ...current.filter((existing)=>existing.id!==order.id)
    ]);

    return order;
  },[profile,sellers,user]);

  const updateOrderStatus=useCallback(async(id,status)=>{
    const {data:order,error:orderError}=await supabase
      .from('orders')
      .select('id,status,delivery_method,pickup_confirmed_at,payment_mode')
      .eq('id',id)
      .single();

    if (orderError) throw orderError;

    if (
      status==='completed'
      && order.delivery_method==='pickup'
    ) {
      throw new Error(
        'Pickup orders must be completed through seller verification.'
      );
    }

    try {
      const result=await updateStripeOrderStatus(id,status,{
        idempotency_key:`order-status-${id}-${status}-${Date.now()}`
      });

      if (!result?.success) {
        throw new Error(result?.error || 'Unable to update order status.');
      }

      await refreshData();

      return result.order;
    } catch (error) {
      console.error('Order status update failed:',error);
      throw error;
    }
  },[refreshData]);

  const sendMessage=async(payload)=>{
    if (!user) {
      throw new Error('Authentication required.');
    }

    const message=typeof payload==='string'
      ? {receiver_id:null,body:payload}
      : payload;

    const {error}=await supabase
      .from('messages')
      .insert({...message,sender_id:user.id});

    if (error) throw error;

    await refreshData();
  };

  const markMessagesAsRead=async(senderId)=>{
    if (!user || !senderId) return;

    const {error}=await supabase
      .from('messages')
      .update({read_at:new Date().toISOString()})
      .eq('sender_id',senderId)
      .eq('receiver_id',user.id)
      .is('read_at',null);

    if (error) throw error;

    await refreshData();
  };

  const addReview=async(review)=>{
    const {data,error}=await supabase
      .from('reviews')
      .insert(review)
      .select()
      .single();

    if (error) throw error;

    await refreshData();

    return data;
  };

  const adminFetchUsers=useCallback(
    ()=>callAdminRpc('admin_get_user_list'),
    []
  );

  const adminFetchOfferings=useCallback(
    ()=>callAdminRpc('admin_get_offerings_full'),
    []
  );

  const adminFetchReviews=useCallback(
    ()=>callAdminRpc('admin_get_reviews_full'),
    []
  );

  const adminFetchFinanceSummary=useCallback(async()=>{
    const data=await callAdminRpc('admin_get_finance_summary');

    return Array.isArray(data) ? data[0] || null : data;
  },[]);

  const adminFetchTransactions=useCallback(
    ()=>callAdminRpc('admin_get_transaction_log'),
    []
  );

  const adminFetchSellerAnalytics=useCallback(
    ()=>callAdminRpc('admin_get_seller_analytics'),
    []
  );

  const adminFetchAllOrders=useCallback(
    ()=>callAdminRpc('admin_get_all_orders_detailed'),
    []
  );

  const adminUpdateUserRole=useCallback(
    (userId,role,isSeller)=>callAdminRpc(
      'admin_update_user_role',
      {
        target_user_id:userId,
        new_role:role,
        new_is_seller:isSeller
      }
    ),
    []
  );

  const adminDeleteUser=useCallback(async(userId,pin,confirmationEmail)=>{
    const {data,error}=await supabase.functions.invoke(
      'admin-delete-user',
      {
        body:{
          user_id:userId,
          pin,
          confirmation_email:confirmationEmail
        }
      }
    );

    if (error) {
      let message=error.message;

      if (error.context instanceof Response) {
        const payload=await error.context.clone().json().catch(()=>null);
        message=payload?.error || message;
      }

      throw new Error(message || 'Unable to delete this account.');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Unable to delete this account.');
    }

    return data;
  },[]);

  const adminToggleOffering=useCallback(
    (offeringId,isActive)=>callAdminRpc(
      'admin_toggle_offering_status',
      {
        target_id:offeringId,
        new_status:isActive
      }
    ),
    []
  );

  const adminDeleteOffering=useCallback(
    (offeringId)=>callAdminRpc(
      'admin_delete_offering',
      {target_id:offeringId}
    ),
    []
  );

  const adminDeleteReview=useCallback(
    (reviewId)=>callAdminRpc('admin_delete_review',{target_id:reviewId}),
    []
  );

  const adminAddCategory=useCallback(
    (name,slug,icon)=>callAdminRpc(
      'admin_add_category',
      {
        cat_name:name,
        cat_slug:slug,
        cat_icon:icon
      }
    ),
    []
  );

  const adminDeleteCategory=useCallback(
    (categoryId)=>callAdminRpc(
      'admin_delete_category',
      {target_id:categoryId}
    ),
    []
  );

  const value={
    sellers,
    offerings,
    categories,
    reviews,
    orders,
    messages,
    pickupNotifications,
    isDataReady,
    refreshData,
    updateSellerProfile,
    addOffering,
    updateOffering,
    deleteOffering,
    createOrder,
    updateOrderStatus,
    sendMessage,
    markMessagesAsRead,
    addReview,
    refreshPickupNotifications,
    markPickupNotificationRead,
    adminFetchUsers,
    adminUpdateUserRole,
    adminDeleteUser,
    adminFetchOfferings,
    adminToggleOffering,
    adminDeleteOffering,
    adminFetchReviews,
    adminDeleteReview,
    adminFetchFinanceSummary,
    adminFetchTransactions,
    adminFetchSellerAnalytics,
    adminFetchAllOrders,
    adminAddCategory,
    adminDeleteCategory
  };

  return (
    <SupabaseDataContext.Provider value={value}>
      {children}
    </SupabaseDataContext.Provider>
  );
};
